import { createContext, useState, useEffect, useMemo } from "react";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0", //$2
          200: "#c2c2c2",
          300: "#a3a3a3", //$2
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#2E1B2E", //$2
          500: "#4A2C4F", //$2s
          600: "#2E1B2E", //$2
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#F48FB1", //$2
          500: "#F48FB1", //$2
          600: "#b22c59", //$2
          700: "#2E1B2E", //$2
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#F48FB1", //$2
          600: "#535ac8",
          700: "#860086", //$2
          800: "#2a2d64",
          900: "#151632",
        },
        tileBackgroundColor:"#2E1B2E", //$2
        tileBorderColor: "#F48FB1", //$2
        dragAndDropDrop: 
        {
          droppableAreaBackgroundColor:"#4A2C4F", //$2
          tileBackgroundColor: "#2e1b2e", //$2
          tileBackgroundColorHover: "#4f164f", //$2
        },
        inputModal: 
        {
          dialogContentBackgroundColor: "#331338", //$2      
          dialogHeaderBackgroundColor: "#331338", //$2     
          inputBackgroundColor: "#331338", //$2
          inputDefaultBorderColor: "#B181B8", //$2
          inputHoverBorderColor: "#F2D9F5", //$2  
          inputFocusedBorderColor: "#F2D9F5", //$2   
        },
        tableRowBottomBorberColor:"#745B78", //$2
        tableHeaderBorderColor: "",
        drawerModalBackgroundColor:"#4a364a", //$2
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#040509",
          200: "#080b12",
          300: "#0c101b",
          400: "#f2f0f0", // manually changed
          500: "#FFFFFF",
          600: "#f8fafd",  //#1F2A40
          700: "#727681",
          800: "#a1a4ab",
          900: "#d0d1d5",
        },
        greenAccentORIGINAL: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#FFFFFF",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        greenAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#d9d9d9",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#d9d9d9",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
        tileBackgroundColor:"#f4f4f4",
        tileBorderColor: "#000000",
        dragAndDropDrop: 
        {
          droppableAreaBackgroundColor:"#e8e9ed", 
          tileBackgroundColor: "#FFFFFF",
          tileBackgroundColorHover: "#ececec"
        },
        inputModal: 
        {
          dialogContentBackgroundColor: "f9f9f9",        // The pop up window background color
          dialogHeaderBackgroundColor: "#000000",        // The pop up window background color
          inputBackgroundColor: "#ffffff",  // This is the background color of the input box
          inputDefaultBorderColor: "#000000",      // This is the default border color of the input box
          inputHoverBorderColor: "#0077CC",        // This is the border color of the input box when hover
          inputFocusedBorderColor: "#0077CC"       // This is the border color of the input box when focused
        },
        tableRowBottomBorberColor:"#000000",
        tableHeaderBorderColor: "#707070",
        drawerModalBackgroundColor:"#FFFFFF"
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[500],
            },
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[500],
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
  };
};

// context for color mode

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});


export const useMode = () => {
  //const [mode, setMode] = useState("dark");

  const [mode, setMode] = useState(() => localStorage.getItem('theme.palette.mode') || 'dark');

  // Update localStorage when the mode changes
  useEffect(() => {
    localStorage.setItem('theme.palette.mode', mode);
  }, [mode]);
  
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
